import { useState } from "react";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import apiClient from "../client";

export const BILLING_QUERY_KEY = ["billing"] as const;
export const BILLING_INVOICES_QUERY_KEY = ["billing", "invoices"] as const;
export const BILLING_PAYMENT_METHOD_QUERY_KEY = ["billing", "payment-method"] as const;

// Matches the API's default page size. Stripe paginates by cursor, so "Load more" passes the id of the
// last invoice we've seen rather than an offset.
const INVOICE_PAGE_SIZE = 10;

export type BillingInterval = "Monthly" | "Yearly" | "Unknown" | string;

export type BillingContainerStatus =
  | "Active"
  | "PastDue"
  | "Cancelled"
  | "Incomplete"
  | "Unknown"
  | string;

/** What an offer takes off this line, as Stripe will bill it. */
export interface BillingLineDiscountDto {
  offerName: string;
  discountType: number;
  discountValue: number;
  durationMonths: number | null;
  amountUsd: number;
  /** When the line goes back to full price. Null for a discount that runs until it is taken off. */
  endsAt: string | null;
}

export interface BillingLineDto {
  // The line's own id: two lines can share a strategy or tier once one is bought again after the first ended.
  id: string;
  // A strategy's line carries its strategy id; a membership's carries its tier id instead, and an add-on's its
  // add-on id.
  tradingStrategyId: string | null;
  membershipTierId: string | null;
  addOnId: string | null;
  // The strategy's name, or on a membership's line the tier's, or on an add-on's its own.
  strategyName: string;
  billingCycle: "Monthly" | "Yearly" | null;
  priceUsd: number | null;
  // Part of the next charge. False for a cancelled line — access runs to activeTo but it won't bill again.
  willRenew: boolean;
  activeTo: string | null;
  cancelledAt: string | null;
  discount: BillingLineDiscountDto | null;
  // When the line moves to its own yearly subscription. Such a line won't bill on this one again.
  yearlyFrom: string | null;
  // On a strategy's line, every membership the customer holds that now includes it. Empty otherwise.
  includedInMemberships: string[];
}

// A yearly subscription booked by an upgrade, waiting for the month already paid for to end.
export interface BillingWaitingYearlyDto {
  tradingStrategyId: string;
  strategyName: string;
  startsOn: string;
  // The yearly list price.
  priceUsd: number;
  // What Stripe will take on startsOn, offer included, or once that has failed, what it is still trying to
  // take. Null when Stripe couldn't be asked.
  firstPaymentUsd: number | null;
  // Stripe couldn't take the first payment and is retrying it.
  firstPaymentFailed: boolean;
}

// One Stripe subscription: the monthly bundle holding many strategies, or a single annual strategy.
export interface BillingContainerDto {
  interval: BillingInterval;
  status: BillingContainerStatus;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  recurringTotalUsd: number;
  lines: BillingLineDto[];
  // For one past due, what Stripe is still trying to take for the bill due on currentPeriodEnd.
  unpaidUsd: number | null;
}

export interface BillingSummaryDto {
  // The Stripe customer is created at first checkout — false means there is nothing to manage yet.
  hasBillingAccount: boolean;
  nextPaymentDate: string | null;
  nextPaymentAmountUsd: number | null;
  /** Credit on the account, taken off the next payments first. Already off nextPaymentAmountUsd. */
  creditUsd: number;
  containers: BillingContainerDto[];
  waitingYearlies: BillingWaitingYearlyDto[];
  staffManagedLines: BillingLineDto[];
}

export interface InvoiceDto {
  id: string;
  number: string | null;
  date: string;
  amountUsd: number;
  status: string;
  // Stripe's hosted invoice page and PDF. Null while an invoice is still a draft.
  viewUrl: string | null;
  downloadUrl: string | null;
}

interface InvoicePageResponse {
  items: InvoiceDto[];
  hasMore: boolean;
}

export interface CardDto {
  brand: string;
  last4: string;
  expiryMonth: number;
  expiryYear: number;
}

export interface PaymentMethodDto {
  // Stripe's type slug: "card", "link", "us_bank_account".
  type: string;
  // Only a bare card carries brand/last4. A card saved through Link lives in the customer's Link wallet,
  // which Stripe doesn't expose — so the method is real and charged, with nothing card-shaped to show.
  card: CardDto | null;
}

interface PaymentMethodResponse {
  // Null only when there genuinely is nothing on file. Anything else means the customer is covered.
  paymentMethod: PaymentMethodDto | null;
}

interface PortalSessionResponse {
  url: string;
}

/**
 * `enabled` is for callers that only need the summary in one branch — My strategies asks
 * for it solely to tell a real post-checkout wait from a stale marker, and an ordinary visit
 * shouldn't pay for a Stripe read it will never look at.
 */
export function useBillingSummary(options?: { enabled?: boolean }) {
  return useQuery<BillingSummaryDto>({
    queryKey: BILLING_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.get<BillingSummaryDto>("/customers/me/billing");
      return res.data;
    },
    enabled: options?.enabled ?? true,
  });
}

/**
 * Invoices are read live from Stripe — we don't mirror them, so this is always the real billing history.
 * Cursor-paginated: each page after the first is fetched with the id of the last invoice already loaded.
 */
export function useBillingInvoices() {
  return useInfiniteQuery({
    queryKey: BILLING_INVOICES_QUERY_KEY,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const res = await apiClient.get<InvoicePageResponse>("/customers/me/billing/invoices", {
        params: {
          limit: INVOICE_PAGE_SIZE,
          ...(pageParam ? { startingAfter: pageParam } : {}),
        },
      });
      return res.data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.items[lastPage.items.length - 1]?.id : undefined,
  });
}

export function useBillingPaymentMethod() {
  return useQuery<PaymentMethodResponse>({
    queryKey: BILLING_PAYMENT_METHOD_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.get<PaymentMethodResponse>(
        "/customers/me/billing/payment-method"
      );
      return res.data;
    },
  });
}

/**
 * Mints a Stripe Customer Portal session. The portal is a Stripe-hosted page that cannot be framed, so
 * callers send the browser to the returned URL rather than embedding it.
 */
export function useBillingPortalSession() {
  return useMutation({
    mutationFn: async ({
      returnUrl,
      updateCard,
    }: {
      returnUrl: string;
      updateCard: boolean;
    }): Promise<PortalSessionResponse> => {
      const res = await apiClient.post<PortalSessionResponse>(
        "/customers/me/billing/portal-session",
        { returnUrl, updateCard }
      );
      return res.data;
    },
  });
}

/**
 * Sends the customer to Stripe's Customer Portal in a new tab, coming back to the page they left. The blank
 * tab is opened synchronously inside the click, since opening it after the await would be swallowed by pop-up
 * blockers, and only then pointed at the minted URL. updateCard opens it straight on changing the card.
 */
export function useOpenBillingPortal({ updateCard = false }: { updateCard?: boolean } = {}) {
  const portalSession = useBillingPortalSession();
  const [failed, setFailed] = useState(false);

  const open = () => {
    setFailed(false);

    // Deliberately NO "noopener" feature here: passing it makes window.open return null, which would lose
    // the handle and send this tab to Stripe instead. The same protection comes from severing the child's
    // back-reference below.
    const tab = window.open("", "_blank");
    if (tab) {
      tab.opener = null;
    }

    const returnUrl = `${window.location.origin}${window.location.pathname}`;
    portalSession.mutate({ returnUrl, updateCard }, {
      onSuccess: ({ url }) => {
        if (tab && !tab.closed) {
          tab.location.href = url;
        } else {
          // Pop-up blocked, or the blank tab was closed while the session was being minted — fall back
          // to this tab; Stripe's return_url brings them back.
          window.location.assign(url);
        }
      },
      onError: () => {
        tab?.close();
        setFailed(true);
      },
    });
  };

  return { open, isPending: portalSession.isPending, failed };
}

export function formatUsd(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

export function billingIntervalLabel(interval: BillingInterval): string {
  return interval === "Yearly" ? "Annual" : "Monthly";
}

// The container's own health, shown as a badge. Past due is the one the customer must act on — it's what
// the portal's card update is for.
export function billingStatusColor(
  status: BillingContainerStatus
): "success" | "danger" | "warning" | "secondary" {
  switch (status) {
    case "Active":
      return "success";
    case "PastDue":
      return "danger";
    case "Incomplete":
      return "warning";
    default:
      return "secondary";
  }
}

export function billingStatusLabel(status: BillingContainerStatus): string {
  switch (status) {
    case "PastDue":
      return "Payment failed";
    case "Incomplete":
      return "Awaiting payment";
    default:
      return status;
  }
}

// Stripe's invoice status is lowercase ("paid", "open", "void"…). Title-cased for the table.
export function invoiceStatusLabel(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function invoiceStatusColor(
  status: string
): "success" | "warning" | "danger" | "secondary" {
  switch (status) {
    case "paid":
      return "success";
    case "open":
      return "warning";
    case "uncollectible":
      return "danger";
    default:
      return "secondary";
  }
}

// Stripe returns the payment method type as a lowercase snake_case slug ("card", "link",
// "us_bank_account"). Used where there are no card details to show, so the page names what is on file
// instead of falling silent.
export function paymentMethodTypeLabel(type: string): string {
  switch (type) {
    case "card":
      return "Card";
    case "link":
      return "Link";
    case "us_bank_account":
      return "Bank account";
    default:
      return type
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
  }
}

// Stripe returns the card brand as a lowercase slug ("visa", "mastercard", "amex").
export function cardBrandLabel(brand: string): string {
  switch (brand) {
    case "amex":
      return "American Express";
    case "mastercard":
      return "Mastercard";
    case "visa":
      return "Visa";
    default:
      return brand.charAt(0).toUpperCase() + brand.slice(1);
  }
}
